import React from 'react';
import { Card, Row, Col, Statistic, Typography, Spin } from 'antd';
import { 
  UserOutlined, 
  BookOutlined, 
  MessageOutlined, 
  TrophyOutlined,
  TeamOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { getDashboardStats } from './api';

const { Title } = Typography;

interface DashboardStats {
  totalUsers: number;
  totalCourses: number;
  totalMessages: number;
  totalLessons: number;
  totalTests: number;
  totalOpenQuestions: number;
  activeUsers: number;
  completedCourses: number;
  newUsersToday: number;
  newUsersYesterday: number;
  uniqueAiUsersToday: number;
  uniqueAiUsersYesterday: number;
  uniqueTestUsersToday: number;
  uniqueTestUsersYesterday: number;
}

interface DashboardProps {
  onCreateCourse?: () => void;
  onManageUsers?: () => void;
  onViewMessages?: () => void;
  onViewStats?: () => void;
}

export default function Dashboard({ 
  onCreateCourse, 
  onManageUsers, 
  onViewMessages, 
  onViewStats 
}: DashboardProps) {
  // Загружаем данные для статистики
  const { data: dashboardStats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: getDashboardStats,
  });

  // Используем данные из API или значения по умолчанию
  const stats: DashboardStats = {
    totalUsers: dashboardStats?.totalUsers || 0,
    totalCourses: dashboardStats?.totalCourses || 0,
    totalMessages: dashboardStats?.totalMessages || 0,
    totalLessons: dashboardStats?.totalLessons || 0,
    totalTests: dashboardStats?.totalTests || 0,
    totalOpenQuestions: 0, // Пока не реализовано в API
    activeUsers: dashboardStats?.activeUsers || 0,
    completedCourses: 0, // Пока не реализовано
    newUsersToday: dashboardStats?.newUsersToday || 0,
    newUsersYesterday: dashboardStats?.newUsersYesterday || 0,
    uniqueAiUsersToday: dashboardStats?.uniqueAiUsersToday || 0,
    uniqueAiUsersYesterday: dashboardStats?.uniqueAiUsersYesterday || 0,
    uniqueTestUsersToday: dashboardStats?.uniqueTestUsersToday || 0,
    uniqueTestUsersYesterday: dashboardStats?.uniqueTestUsersYesterday || 0,
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <Title level={2} style={{ color: "#1976d2", margin: 0 }}>
          📊 Панель управления
        </Title>
        <p style={{ color: "#666", marginTop: 8, fontSize: 16 }}>
          Обзор основных показателей платформы ELM.TJ
        </p>
      </div>

      {/* Основные показатели */}
      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card 
            style={{ 
              borderRadius: 12, 
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: 'none'
            }}
            bodyStyle={{ padding: 24 }}
          >
            <Statistic
              title={
                <span style={{ fontSize: 16, color: '#666' }}>
                  <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                  Всего пользователей
                </span>
              }
              value={stats.totalUsers}
              valueStyle={{ 
                color: '#1890ff', 
                fontSize: 32, 
                fontWeight: 'bold' 
              }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card 
            style={{ 
              borderRadius: 12, 
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: 'none'
            }}
            bodyStyle={{ padding: 24 }}
          >
            <Statistic
              title={
                <span style={{ fontSize: 16, color: '#666' }}>
                  <BookOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                  Курсов
                </span>
              }
              value={stats.totalCourses}
              valueStyle={{ 
                color: '#52c41a', 
                fontSize: 32, 
                fontWeight: 'bold' 
              }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card 
            style={{ 
              borderRadius: 12, 
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: 'none'
            }}
            bodyStyle={{ padding: 24 }}
          >
            <Statistic
              title={
                <span style={{ fontSize: 16, color: '#666' }}>
                  <MessageOutlined style={{ marginRight: 8, color: '#722ed1' }} />
                  Сообщений ИИ
                </span>
              }
              value={stats.totalMessages}
              valueStyle={{ 
                color: '#722ed1', 
                fontSize: 32, 
                fontWeight: 'bold' 
              }}
            />
          </Card>
        </Col>

                 <Col xs={24} sm={12} lg={6}>
           <Card 
             style={{ 
               borderRadius: 12, 
               boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
               border: 'none'
             }}
             bodyStyle={{ padding: 24 }}
           >
                                <Statistic
                     title={
                       <span style={{ fontSize: 16, color: '#666' }}>
                         <TeamOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
                         Активных пользователей
                       </span>
                     }
                     value={stats.activeUsers}
                     valueStyle={{ 
                       color: '#fa8c16', 
                       fontSize: 32, 
                       fontWeight: 'bold' 
                     }}
                   />
                   <div style={{ 
                     textAlign: 'center', 
                     padding: '4px', 
                     backgroundColor: '#fff7e6', 
                     borderRadius: 4, 
                     marginTop: 8,
                     border: '1px solid #ffd591'
                   }}>
                     <div style={{ color: '#fa8c16', fontSize: 11 }}>
                       7 дней: ИИ-чат, тесты или прогресс
                     </div>
                   </div>
           </Card>
         </Col>


      </Row>

      {/* Дополнительная статистика */}
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} lg={8}>
          <Card 
            title={
              <span>
                <FileTextOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                Контент платформы
              </span>
            }
            style={{ 
              borderRadius: 12, 
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: 'none'
            }}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Уроков"
                  value={stats.totalLessons}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Тестов"
                  value={stats.totalTests}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Открытых вопросов"
                  value={stats.totalOpenQuestions}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Завершенных курсов"
                  value={stats.completedCourses}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Col>
            </Row>
          </Card>
        </Col>

                 <Col xs={24} sm={12} lg={8}>
           <Card 
             title={
               <span>
                 <ClockCircleOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                 Активность
               </span>
             }
             style={{ 
               borderRadius: 12, 
               boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
               border: 'none'
             }}
           >
             <div style={{ textAlign: 'center', padding: '20px 0' }}>
               <div style={{ fontSize: 48, color: '#52c41a', marginBottom: 8 }}>
                 {stats.totalMessages > 0 ? Math.round((stats.totalMessages / stats.totalUsers) * 10) / 10 : 0}
               </div>
               <div style={{ color: '#666', fontSize: 16 }}>
                 Среднее количество сообщений на пользователя
               </div>
             </div>
           </Card>
         </Col>

                        <Col xs={24} sm={12} lg={8}>
                 <Card 
                   title={
                     <span>
                       <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                       Активность пользователей
                     </span>
                   }
                   style={{ 
                     borderRadius: 12, 
                     boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                     border: 'none'
                   }}
                 >
                   <Row gutter={[16, 16]} style={{ padding: '20px 0' }}>
                     <Col span={12}>
                       <div style={{ textAlign: 'center' }}>
                         <div style={{ fontSize: 24, color: '#1890ff', fontWeight: 'bold', marginBottom: 4 }}>
                           {stats.newUsersToday}
                         </div>
                         <div style={{ color: '#666', fontSize: 12 }}>
                           Новых сегодня
                         </div>
                       </div>
                     </Col>
                     <Col span={12}>
                       <div style={{ textAlign: 'center' }}>
                         <div style={{ fontSize: 24, color: '#52c41a', fontWeight: 'bold', marginBottom: 4 }}>
                           {stats.newUsersYesterday}
                         </div>
                         <div style={{ color: '#666', fontSize: 12 }}>
                           Новых вчера
                         </div>
                       </div>
                     </Col>
                   </Row>
                   
                   <div style={{ 
                     padding: '12px', 
                     backgroundColor: '#f0f5ff', 
                     borderRadius: 8, 
                     marginTop: 16,
                     border: '1px solid #adc6ff'
                   }}>
                     <div style={{ color: '#1890ff', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                       Уникальные пользователи с ИИ:
                     </div>
                     <Row gutter={[16, 16]}>
                       <Col span={12}>
                         <div style={{ textAlign: 'center' }}>
                           <div style={{ fontSize: 20, color: '#1890ff', fontWeight: 'bold' }}>
                             {stats.uniqueAiUsersToday}
                           </div>
                           <div style={{ color: '#666', fontSize: 11 }}>
                             Сегодня
                           </div>
                         </div>
                       </Col>
                       <Col span={12}>
                         <div style={{ textAlign: 'center' }}>
                           <div style={{ fontSize: 20, color: '#52c41a', fontWeight: 'bold' }}>
                             {stats.uniqueAiUsersYesterday}
                           </div>
                           <div style={{ color: '#666', fontSize: 11 }}>
                             Вчера
                           </div>
                         </div>
                       </Col>
                     </Row>
                   </div>
                   
                   <div style={{ 
                     padding: '12px', 
                     backgroundColor: '#fff7e6', 
                     borderRadius: 8, 
                     marginTop: 12,
                     border: '1px solid #ffd591'
                   }}>
                     <div style={{ color: '#fa8c16', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                       Уникальные пользователи с тестами:
                     </div>
                     <Row gutter={[16, 16]}>
                       <Col span={12}>
                         <div style={{ textAlign: 'center' }}>
                           <div style={{ fontSize: 20, color: '#fa8c16', fontWeight: 'bold' }}>
                             {stats.uniqueTestUsersToday}
                           </div>
                           <div style={{ color: '#666', fontSize: 11 }}>
                             Сегодня
                           </div>
                         </div>
                       </Col>
                       <Col span={12}>
                         <div style={{ textAlign: 'center' }}>
                           <div style={{ fontSize: 20, color: '#52c41a', fontWeight: 'bold' }}>
                             {stats.uniqueTestUsersYesterday}
                           </div>
                           <div style={{ color: '#666', fontSize: 11 }}>
                             Вчера
                           </div>
                         </div>
                       </Col>
                     </Row>
                   </div>
                   
                   <div style={{ 
                     textAlign: 'center', 
                     padding: '8px', 
                     backgroundColor: '#f6ffed', 
                     borderRadius: 8, 
                     marginTop: 12,
                     border: '1px solid #b7eb8f'
                   }}>
                     <div style={{ color: '#666', fontSize: 11 }}>
                       Активные = ИИ-чат, тесты или прогресс за 7 дней
                     </div>
                   </div>
                 </Card>
               </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card 
            title={
              <span>
                <CheckCircleOutlined style={{ marginRight: 8, color: '#722ed1' }} />
                Статус системы
              </span>
            }
            style={{ 
              borderRadius: 12, 
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: 'none'
            }}
          >
            <div style={{ padding: '20px 0' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: 16,
                padding: '12px',
                backgroundColor: '#f6ffed',
                borderRadius: 8,
                border: '1px solid #b7eb8f'
              }}>
                <div style={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: '#52c41a',
                  marginRight: 12 
                }} />
                <span style={{ color: '#52c41a', fontWeight: 500 }}>
                  Система работает нормально
                </span>
              </div>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: 16,
                padding: '12px',
                backgroundColor: '#f0f5ff',
                borderRadius: 8,
                border: '1px solid #adc6ff'
              }}>
                <div style={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: '#1890ff',
                  marginRight: 12 
                }} />
                <span style={{ color: '#1890ff', fontWeight: 500 }}>
                  База данных подключена
                </span>
              </div>

              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                padding: '12px',
                backgroundColor: '#fff7e6',
                borderRadius: 8,
                border: '1px solid #ffd591'
              }}>
                <div style={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: '#fa8c16',
                  marginRight: 12 
                }} />
                <span style={{ color: '#fa8c16', fontWeight: 500 }}>
                  ИИ-сервисы активны
                </span>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Последние действия */}
      <Row style={{ marginTop: 32 }}>
        <Col span={24}>
          <Card 
            title={
              <span>
                <TrophyOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
                Быстрые действия
              </span>
            }
            style={{ 
              borderRadius: 12, 
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: 'none'
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Card 
                  size="small" 
                  style={{ 
                    textAlign: 'center', 
                    cursor: 'pointer',
                    borderRadius: 8,
                    border: '1px solid #f0f0f0'
                  }}
                  hoverable
                  onClick={onCreateCourse}
                >
                  <BookOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                  <div>Создать курс</div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card 
                  size="small" 
                  style={{ 
                    textAlign: 'center', 
                    cursor: 'pointer',
                    borderRadius: 8,
                    border: '1px solid #f0f0f0'
                  }}
                  hoverable
                  onClick={onManageUsers}
                >
                  <UserOutlined style={{ fontSize: 24, color: '#52c41a', marginBottom: 8 }} />
                  <div>Управление пользователями</div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card 
                  size="small" 
                  style={{ 
                    textAlign: 'center', 
                    cursor: 'pointer',
                    borderRadius: 8,
                    border: '1px solid #f0f0f0'
                  }}
                  hoverable
                  onClick={onViewMessages}
                >
                  <MessageOutlined style={{ fontSize: 24, color: '#722ed1', marginBottom: 8 }} />
                  <div>Просмотр сообщений</div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card 
                  size="small" 
                  style={{ 
                    textAlign: 'center', 
                    cursor: 'pointer',
                    borderRadius: 8,
                    border: '1px solid #f0f0f0'
                  }}
                  hoverable
                  onClick={onViewStats}
                >
                  <TrophyOutlined style={{ fontSize: 24, color: '#fa8c16', marginBottom: 8 }} />
                  <div>Статистика</div>
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
